import { ReactElement } from 'react';
import { AutocompleteInput, FilterPayload, ReferenceInput, required } from 'react-admin';
import { useWatch } from 'react-hook-form';
import { SoftwareComponent } from 'types';

export interface SoftwareVersionInputProps {
  component: SoftwareComponent;
  /**
   * Pre-selected version, applied as the input's own default value. Only the create form uses
   * it (a clone carries its source release's selection); the edit form seeds the whole
   * selection through the form's `defaultValues` instead, so that clearing it on a hardware
   * change cannot be undone by an input re-applying its default. Unaffected by TVR-2156: the clone
   * map has always been keyed by component id, only the field path it lands under changed.
   */
  defaultValue?: number;
  /**
   * Form field this input writes to. Required, and deliberately without a default.
   *
   * Both callers pass `versionFieldName(component)` from `./componentVersions`, which yields
   * `component_versions.c<id>`. TVR-2156 removed the reason this used to have no default: the
   * create form passed the component's own name (`tover.tafel`), which react-hook-form reads as a
   * *path* and nests under a `tover` object, and its toolbar dug the value back out with
   * `tover[name.split('.')[1]]`. Nothing splits a component name any more.
   *
   * It stays required anyway. The one thing a default would have to pick — the namespace the value
   * lands in — is what the two forms must agree on, and an input that silently chooses it for a new
   * caller is how they disagreed in the first place.
   */
  source: string;
  /** A string is translated by react-admin; wrap a literal in a fragment to render it as-is. */
  label?: string | ReactElement | false;
  /**
   * Whether a component the hardware marks as required must carry a version before the form can
   * be submitted. The create form always enforces it — a new release has to be complete. The edit
   * form turns it off for a release that carries no versions at all, which the API deliberately
   * leaves renameable: a client-side `required` would be the only thing standing between such a
   * release and a rename.
   */
  enforceRequired?: boolean;
}

/**
 * Picks the version of one software component for a release.
 *
 * The choices follow the release being edited rather than the component alone: they are scoped
 * to the selected hardware, and restricted to tagged versions unless the release is custom — a
 * branch version on a non-custom release is rejected by the API.
 *
 * Shared by `SoftwareReleaseCreate` and `SoftwareReleaseEdit` (TVR-1909).
 */
export const SoftwareVersionInput = ({
  component,
  defaultValue,
  source,
  label,
  enforceRequired = true,
}: SoftwareVersionInputProps) => {
  const isCustom = useWatch({ name: 'custom' });
  const id: number = useWatch({ name: 'hardware_id' });
  const isRequired = enforceRequired && component.required_hardware_ids?.includes(id);

  const filter: FilterPayload = { hardware_id: id, software_component_id: component.id };

  if (!isCustom) {
    filter.type = 'tag';
  }

  return (
    <ReferenceInput source={source} reference="software_versions" filter={filter}>
      <AutocompleteInput
        label={label}
        validate={isRequired ? required() : undefined}
        optionText="name"
        defaultValue={defaultValue}
      />
    </ReferenceInput>
  );
};
